import React, { useState } from 'react';
import { Loader2, Sparkles, ConciergeBell, Shield, Car, BedDouble, LayoutDashboard, User, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { setDemoSession, setDemoAnonymous } from '../lib/session';
import { clearGuidedTourSeen } from '../components/shared/GuidedTour';

// Holiday Inn showroom launcher — no-login. Mirrors the Property
// Management demo (DemoTenantEntry/DemoManagerEntry): each tile fetches an
// isolated demo session from the backend, stores it where the existing app
// reads it, then lands DIRECTLY in the existing experience. No new architecture.
const API = (typeof window !== 'undefined' ? window.location.origin : '');
const RETURN_URL = '/demo/hotel';

async function enterGuest() {
    setDemoAnonymous();
    sessionStorage.removeItem('yulontap_demo_mode');
    sessionStorage.setItem('yulontap_hotel_demo', '1');
    sessionStorage.removeItem('yulontap_auto_demo');
    const res = await fetch(`${API}/api/demo-hotel/guest/session?return_url=${encodeURIComponent(RETURN_URL)}`);
    const d = await res.json();
    if (!res.ok || !d.token) throw new Error('session');
    localStorage.setItem(d.store_key, JSON.stringify({ token: d.token, lang: d.language || 'en' }));
    sessionStorage.setItem('yulontap_demo_return', d.return_url || RETURN_URL);
    window.location.replace(`/assistant/${d.card_id}`);
}

async function enterStaff() {
    // Staff use the EXISTING Staff Hub + demo-card PIN 0000 bypass (hotel_demo.py).
    // Clear any manager token so the PIN screen shows a clean staff entry.
    setDemoAnonymous();
    sessionStorage.removeItem('yulontap_demo_mode');
    sessionStorage.setItem('yulontap_hotel_demo', '1');
    sessionStorage.removeItem('yulontap_auto_demo');
    sessionStorage.setItem('yulontap_demo_return', RETURN_URL);
    const info = await (await fetch(`${API}/api/demo-hotel/staff/info`)).json();
    window.location.replace(`/assistant/staff/${info.card_id || 'HARBOURVIEW1'}`);
}

async function enterManager() {
    const res = await fetch(`${API}/api/demo-hotel/manager/session?return_url=${encodeURIComponent(RETURN_URL)}`);
    const d = await res.json();
    if (!res.ok || !d.access_token) throw new Error('session');
    setDemoSession(d.access_token, d.user || {});
    sessionStorage.setItem('yulontap_demo_mode', '1');
    sessionStorage.setItem('yulontap_hotel_demo', '1');
    sessionStorage.removeItem('yulontap_auto_demo');
    try { localStorage.setItem('yulontap-theme-preference', 'dark'); } catch (_e) { /* ignore */ }
    document.documentElement.classList.add('dark');
    sessionStorage.setItem('yulontap_demo_return', d.return_url || RETURN_URL);
    window.location.replace('/command-center');
}

export default function HotelDemoLauncher() {
    const { t } = useTranslation();
    const [busy, setBusy] = useState('');

    const go = (key, fn) => async () => {
        if (busy) return;
        setBusy(key);
        try { await fn(); } catch (e) { setBusy(''); }
    };

    const staff = [
        { dept: 'concierge', label: t('hotelDemo.concierge', 'Concierge'), Icon: ConciergeBell },
        { dept: 'security', label: t('hotelDemo.security', 'Security'), Icon: Shield },
        { dept: 'valet', label: t('hotelDemo.valet', 'Valet'), Icon: Car },
        { dept: 'housekeeping', label: t('hotelDemo.housekeeping', 'Housekeeping'), Icon: BedDouble },
    ];

    const Tile = ({ testid, onClick, Icon, title, subtitle, accent }) => (
        <button
            data-testid={testid}
            onClick={onClick}
            disabled={!!busy}
            className={`group relative flex flex-col items-start gap-3 rounded-2xl border p-5 text-left transition-all active:scale-[0.98] disabled:opacity-60 ${accent}`}
        >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 border border-white/15">
                {busy === testid ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
            </span>
            <span className="text-lg font-bold leading-tight">{title}</span>
            <span className="text-xs text-white/60">{subtitle}</span>
        </button>
    );

    return (
        <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center px-6 py-14" data-testid="hotel-demo-launcher">
            <div className="w-full max-w-3xl">
                <div className="flex items-center gap-2 text-amber-300 mb-3">
                    <Sparkles className="h-5 w-5" />
                    <span className="text-xs font-bold uppercase tracking-[0.25em]">{t('hotelDemo.badge', 'Showroom Demo')}</span>
                </div>
                <h1 className="text-4xl sm:text-5xl font-black tracking-tight">{FS_TITLE}</h1>
                <p className="mt-3 text-sm text-white/60 max-w-xl">
                    {t('hotelDemo.subtitle', 'Experience the full loop: a Guest submits a request, Staff handle it, Management sees the same activity in the Command Center, and the Guest sees the update — all on the real YulOnTap systems.')}
                </p>

                <h2 className="mt-10 mb-3 text-sm font-semibold uppercase tracking-wider text-white/50">{t('hotelDemo.guestSection', 'Guest')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Tile testid="hotel-demo-guest" onClick={go('hotel-demo-guest', enterGuest)} Icon={User}
                        title={t('hotelDemo.guest', 'Hotel Guest')}
                        subtitle={t('hotelDemo.guestSub', 'Open the guest experience and submit a request')}
                        accent="border-amber-400/40 bg-amber-500/10 hover:bg-amber-500/15" />
                </div>

                <h2 className="mt-10 mb-3 text-sm font-semibold uppercase tracking-wider text-white/50">{t('hotelDemo.staffSection', 'Hotel Staff')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Tile testid="hotel-demo-staff" onClick={go('hotel-demo-staff', enterStaff)} Icon={ConciergeBell}
                        title={t('hotelDemo.staff', 'Staff Hub')}
                        subtitle={t('hotelDemo.staffSub', 'Concierge · Security · Valet · Housekeeping — demo PIN 0000')}
                        accent="border-emerald-400/30 bg-emerald-500/10 hover:bg-emerald-500/15" />
                    <div className="flex flex-wrap items-center gap-2 self-center">
                        {staff.map(({ dept, label, Icon }) => (
                            <span key={dept} data-testid={`hotel-demo-dept-${dept}`}
                                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/70">
                                <Icon className="h-3.5 w-3.5" />{label}
                            </span>
                        ))}
                    </div>
                </div>

                <h2 className="mt-10 mb-3 text-sm font-semibold uppercase tracking-wider text-white/50">{t('hotelDemo.mgmtSection', 'Management')}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Tile testid="hotel-demo-manager" onClick={go('hotel-demo-manager', enterManager)} Icon={LayoutDashboard}
                        title={t('hotelDemo.manager', 'Command Center')}
                        subtitle={t('hotelDemo.managerSub', 'Management oversight of the hotel operation')}
                        accent="border-sky-400/30 bg-sky-500/10 hover:bg-sky-500/15" />
                </div>

                {/* Presenter control: a deterministic start state before a meeting.
                    Closes any request left open by an earlier run (nothing is deleted)
                    so the Security tile starts GREEN and the presenter's own request
                    drives RED → YELLOW → GREEN. */}
                <div className="mt-10 flex flex-wrap items-center gap-3">
                    <button type="button" data-testid="hotel-demo-reset"
                        onClick={async () => {
                            const el = document.querySelector('[data-testid="hotel-demo-reset-state"]');
                            if (el) el.textContent = t('hotelDemo.resetting', 'Resetting…');
                            try {
                                const r = await fetch(`${API}/api/demo-hotel/reset`, { method: 'POST' });
                                // Fresh demo = first visit: replay the guided tour.
                                clearGuidedTourSeen();
                                if (el) el.textContent = r.ok
                                    ? t('hotelDemo.resetDone', 'Clean start — every department is green.')
                                    : t('hotelDemo.resetFailed', 'Reset failed. Please try again.');
                            } catch {
                                if (el) el.textContent = t('hotelDemo.resetFailed', 'Reset failed. Please try again.');
                            }
                        }}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10 transition-colors">
                        <RotateCcw className="h-4 w-4" />{t('hotelDemo.resetDemo', 'Reset demo state')}
                    </button>
                    <span data-testid="hotel-demo-reset-state" className="text-xs text-white/60">
                        {t('hotelDemo.resetHint', 'Closes anything left open by an earlier run. Nothing is deleted.')}
                    </span>
                </div>
            </div>
        </div>
    );
}

const FS_TITLE = 'Grand Demo Hotel';
